import { t } from "@lingui/macro";
import {
  BudgetItemTypeEnum,
  type Maybe,
  PreviewProjectTimeBillingDocument,
  type PreviewProjectTimeBillingInput,
  type PreviewProjectTimeBillingQuery,
  type PreviewProjectTimeBillingQueryVariables,
  type ProjectTimeBillingComputedFragment,
  type ProjectTimeBillingDetailFragment,
  type TimeBillingComponentInput,
  type TimeBillingComponentKeyEnum,
  type TimeBillingSourceInput,
  TimeBillingSourceTypeEnum,
  TimeBillingTypeEnum,
  type UpdateProjectTimeBillingInput,
} from "@src/__generated__/graphql";
import {
  decodeOiBudgetItemKey,
  encodeOiBudgetItemKey,
} from "@src/components/modules/projects/time-billing/components/sources-edit-modal/oi-budget-item-key";
import type { IOption } from "@src/components/ui-kit";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { currency, ONE_HOUR_IN_SECONDS } from "@src/utils/formatters";
import { NumberModel, TimeFieldState } from "@src/utils/models";
import debounce from "lodash/debounce";
import { action, computed, makeObservable, observable, reaction } from "mobx";
import { isUnsavedId, type TimeBillingStore } from "../../store";
import type {
  ComponentState,
  FetchPreviewOptions,
  PreviewApplyMode,
  UserRowState,
} from "./types";
import {
  getExpenseItemDisplayName,
  getOiBudgetItemDisplayName,
  mapPreviewRowsToUserRows,
  transformComponents,
} from "./utils";

export class ExpandedRowStore {
  private parentStore: TimeBillingStore;
  private itemId: string;

  @observable.ref
  type: TimeBillingTypeEnum = TimeBillingTypeEnum.Auto;

  @observable.ref
  title = "";

  @observable.ref
  finalTotalNote = "";

  @observable.ref
  selectedOiBudgetItemKeys: string[] = [];

  @observable.ref
  selectedExpenseItemIds: string[] = [];

  @observable.ref
  components: ComponentState[] = [];

  @observable.ref
  userRows: UserRowState[] = [];

  @observable.ref
  previewUserRows: ProjectTimeBillingComputedFragment["userRows"] | null = null;

  @observable.ref
  selectedTimeTrackingItemIds: string[] = [];

  @observable.ref
  previewSources: ProjectTimeBillingComputedFragment["sources"] | null = null;

  private previewFetcher = new FetchHelper<
    PreviewProjectTimeBillingQuery,
    PreviewProjectTimeBillingQueryVariables
  >(PreviewProjectTimeBillingDocument);

  constructor(parentStore: TimeBillingStore, itemId: string) {
    makeObservable(this);
    this.parentStore = parentStore;
    this.itemId = itemId;

    reaction(
      () => this.detail,
      (detail) => {
        if (detail) {
          this.syncWithDetail(detail);
        }
      },
      { fireImmediately: true },
    );
  }

  @computed
  get detail(): ProjectTimeBillingDetailFragment | undefined {
    return this.parentStore.expandedItemDetails.get(this.itemId);
  }

  @computed
  get isEditing(): boolean {
    return this.parentStore.editingItemId === this.itemId;
  }

  @computed
  get isUnsaved(): boolean {
    return isUnsavedId(this.itemId);
  }

  @computed
  get isTitleValid(): boolean {
    return this.title.trim().length > 0;
  }

  @computed
  get isUpdateLoading(): boolean {
    return this.parentStore.isUpdateLoading;
  }

  @computed
  get isSaveLoading(): boolean {
    return this.isUnsaved
      ? this.parentStore.isCreateLoading
      : this.parentStore.isUpdateLoading;
  }

  @computed
  get isDetailLoading(): boolean {
    return this.parentStore.isItemDetailLoading(this.itemId);
  }

  @computed
  get isPreviewLoading(): boolean {
    return this.previewFetcher.isLoading.value;
  }

  @computed
  get currency() {
    return this.parentStore.appStore.workspaceStore.settings?.currency;
  }

  @computed
  get denomination(): number {
    return this.currency?.denomination ?? 2;
  }

  @computed
  get oiBudgetItemOptions(): IOption[] {
    const sources = this.previewSources ?? this.detail?.sources ?? [];
    return sources
      .filter((s) => s.source_type === TimeBillingSourceTypeEnum.OiBudgetItem)
      .map((s) => {
        const key =
          s.outgoing_invoice_id && s.budget_item_id && s.budget_item_type
            ? encodeOiBudgetItemKey(
                s.outgoing_invoice_id,
                s.budget_item_id,
                s.budget_item_type as BudgetItemTypeEnum,
              )
            : (s.source_id ?? "");
        return {
          value: key,
          label: getOiBudgetItemDisplayName(s),
        };
      });
  }

  @computed
  get expenseItemOptions(): IOption[] {
    const sources = this.previewSources ?? this.detail?.sources ?? [];
    return sources
      .filter((s) => s.source_type === TimeBillingSourceTypeEnum.ExpenseItem)
      .map((s) => ({
        value: s.source_id ?? s.expenseItem?.id ?? "",
        label: getExpenseItemDisplayName(s),
      }));
  }

  @computed
  get selectedOiBudgetItemsTableData() {
    const sources = this.previewSources ?? this.detail?.sources ?? [];
    return sources
      .filter((s) => {
        if (s.source_type !== TimeBillingSourceTypeEnum.OiBudgetItem) {
          return false;
        }
        if (
          !s.outgoing_invoice_id ||
          !s.budget_item_id ||
          !s.budget_item_type
        ) {
          return false;
        }
        const key = encodeOiBudgetItemKey(
          s.outgoing_invoice_id,
          s.budget_item_id,
          s.budget_item_type as BudgetItemTypeEnum,
        );
        return this.selectedOiBudgetItemKeys.includes(key);
      })
      .map((source) => {
        const billingEntity = source.outgoingInvoice?.billingEntity;
        const addressParts = [
          billingEntity?.internal_name,
          billingEntity?.street,
          billingEntity?.city,
        ].filter(Boolean);
        const key = encodeOiBudgetItemKey(
          source.outgoing_invoice_id!,
          source.budget_item_id!,
          source.budget_item_type as BudgetItemTypeEnum,
        );

        return {
          key,
          invoice_no: source.outgoingInvoice?.invoice_no ?? key,
          budget_item_title: source.budgetItem?.title ?? "-",
          budget_item_type: source.budget_item_type,
          date: source.outgoingInvoice?.issue_date ?? "",
          amount: source.amount ?? 0,
          billing_address: addressParts.join(", ") || "-",
        };
      });
  }

  @computed
  get selectedExpenseItemsTableData() {
    const sources = this.previewSources ?? this.detail?.sources ?? [];
    return sources
      .filter(
        (s) =>
          s.source_type === TimeBillingSourceTypeEnum.ExpenseItem &&
          this.selectedExpenseItemIds.includes(
            s.source_id ?? s.expenseItem?.id ?? "",
          ),
      )
      .map((source) => ({
        id: source.source_id ?? source.expenseItem?.id ?? null,
        expense_id: source.expense?.id ?? null,
        expense_no:
          source.expense?.expense_no ??
          source.expense?.document_number ??
          source.expense?.id,
        budget_item_title: source.expenseItem?.budgetItem?.title ?? "-",
        budget_item_type: source.budget_item_type ?? null,
        date: source.expense?.date_of_supply,
        amount: source.amount ?? 0,
        contractor: source.expense?.contractor?.legal_name ?? "-",
        description: source.expenseItem?.description?.title ?? "",
      }));
  }

  @computed
  get selectedOiBudgetItemsTotalAmount(): number {
    return this.selectedOiBudgetItemsTableData.reduce(
      (sum, source) => sum + source.amount,
      0,
    );
  }

  @computed
  get selectedExpenseItemsTotalAmount(): number {
    return this.selectedExpenseItemsTableData.reduce(
      (sum, source) => sum + source.amount,
      0,
    );
  }

  @computed
  get computedTotal(): NumberModel {
    const total = this.components
      .filter((c) => c.use_in_total)
      .reduce((sum, c) => sum + c.amount.denominatedValue, 0);
    return new NumberModel(total, this.denomination);
  }

  @computed
  get selectedOiBudgetItemLabels(): string {
    if (this.selectedOiBudgetItemKeys.length === 0) return "";
    return this.oiBudgetItemOptions
      .filter((o) => this.selectedOiBudgetItemKeys.includes(o.value))
      .map((o) => o.label)
      .join(", ");
  }

  @computed
  get selectedExpenseItemLabels(): string {
    if (this.selectedExpenseItemIds.length === 0) return "";
    return this.expenseItemOptions
      .filter((o) => this.selectedExpenseItemIds.includes(o.value))
      .map((o) => o.label)
      .join(", ");
  }

  @computed
  get userRowsTotalAmount(): NumberModel {
    const total = this.userRowsForDisplay.reduce(
      (prev, { amount }) => prev + amount.denominatedValue,
      0,
    );
    return new NumberModel(total, this.denomination);
  }

  @computed
  get userRowsTotalTrackedTime(): number {
    return this.userRowsForDisplay.reduce(
      (prev, { tracked_time }) => prev + tracked_time,
      0,
    );
  }

  @computed
  get userRowsTotalSpent(): NumberModel {
    const total = this.userRowsForDisplay.reduce(
      (prev, { spent }) => prev + spent.denominatedValue,
      0,
    );
    return new NumberModel(total, this.denomination);
  }

  @computed
  get userRowsTotalInvoicedTime(): number {
    return this.userRowsForDisplay.reduce(
      (prev, { invoiced_time }) => prev + invoiced_time.value,
      0,
    );
  }

  @computed
  get userRowsTotalOverservicedTime(): number {
    return this.userRowsForDisplay.reduce(
      (prev, { overserviced_time }) => prev + overserviced_time.value,
      0,
    );
  }

  @computed
  get userRowsTotalDifference(): number {
    return Math.round(
      this.computedTotal.denominatedValue -
        this.userRowsTotalAmount.denominatedValue,
    );
  }

  @computed
  get userRowsHaveTotalsMismatch(): boolean {
    return this.userRowsTotalDifference !== 0;
  }

  @computed
  get userRowIdsWithTimeMismatch(): Set<string> {
    return new Set(
      this.userRowsForDisplay
        .filter(
          ({ tracked_time, invoiced_time, overserviced_time }) =>
            Math.round(invoiced_time.value) +
              Math.round(overserviced_time.value) !==
            tracked_time,
        )
        .map(({ id }) => id),
    );
  }

  @computed
  get userRowsHaveTimeMismatch(): boolean {
    return this.userRowIdsWithTimeMismatch.size > 0;
  }

  @computed
  get saveBlockedMessage(): string | null {
    if (!this.isTitleValid) return t`Cannot save. Title is required`;
    if (this.userRowsHaveTotalsMismatch) {
      return t`Cannot save. ${this.userRowsTotalDifferenceMessage}`;
    }
    if (this.userRowsHaveTimeMismatch) {
      return t`Cannot save. Invoiced time and overservice must add up to tracked time`;
    }
    return null;
  }

  @computed
  get userRowsTotalDifferenceMessage(): string {
    const formattedDiff = currency.formatByWorkspace(
      Math.abs(this.userRowsTotalDifference),
    );
    return this.userRowsTotalDifference < 0
      ? t`Total is ${formattedDiff} higher than Time Billing to Allocate`
      : t`Total is ${formattedDiff} lower than Time Billing to Allocate`;
  }

  @computed
  get userRowsForDisplay() {
    if (this.previewUserRows) {
      return this.previewUserRows.map((row, index) => {
        const editedRow = this.userRows[index];
        return {
          id: `preview-${row.user_id}-${row.time_tracking_work_type_id ?? "null"}-${row.team_id ?? "null"}`,
          user: row.user,
          position: row.position ?? null,
          team: row.team ?? null,
          tracked_time: row.tracked_time,
          client_rate: NumberModel.fromDenomValue(
            row.client_rate,
            this.denomination,
          ),
          spent: NumberModel.fromDenomValue(row.spent, this.denomination),
          effective_hourly_rate: NumberModel.fromDenomValue(
            editedRow?.amount
              ? row.tracked_time > 0
                ? editedRow.amount.denominatedValue /
                  (row.tracked_time / ONE_HOUR_IN_SECONDS)
                : 0
              : row.effective_hourly_rate,
            this.denomination,
          ),
          amount:
            editedRow?.amount ??
            NumberModel.fromDenomValue(row.amount, this.denomination),
          invoiced_time:
            editedRow?.invoiced_time ?? new TimeFieldState(row.invoiced_time),
          overserviced_time:
            editedRow?.overserviced_time ??
            new TimeFieldState(row.overserviced_time),
          effective_hourly_rate_invoiced:
            editedRow?.effective_hourly_rate_invoiced ??
            NumberModel.fromDenomValue(
              row.effective_hourly_rate_invoiced,
              this.denomination,
            ),
        };
      });
    }

    if (!this.detail) return [];
    return this.detail.userRows.map((row) => {
      const editedRow = this.userRows.find((ur) => ur.id === row.id);
      return {
        id: row.id,
        user: row.user,
        position: row.position ?? null,
        team: row.team ?? null,
        tracked_time: row.tracked_time,
        client_rate: NumberModel.fromDenomValue(
          row.client_rate,
          this.denomination,
        ),
        spent: NumberModel.fromDenomValue(row.spent, this.denomination),
        effective_hourly_rate: NumberModel.fromDenomValue(
          editedRow?.amount
            ? row.tracked_time > 0
              ? editedRow.amount.denominatedValue /
                (row.tracked_time / ONE_HOUR_IN_SECONDS)
              : 0
            : row.effective_hourly_rate,
          this.denomination,
        ),
        amount:
          editedRow?.amount ??
          NumberModel.fromDenomValue(row.amount, this.denomination),
        invoiced_time:
          editedRow?.invoiced_time ?? new TimeFieldState(row.invoiced_time),
        overserviced_time:
          editedRow?.overserviced_time ??
          new TimeFieldState(row.overserviced_time),
        effective_hourly_rate_invoiced:
          editedRow?.effective_hourly_rate_invoiced ??
          NumberModel.fromDenomValue(
            row.effective_hourly_rate_invoiced,
            this.denomination,
          ),
      };
    });
  }

  @action.bound
  syncWithDetail(detail: ProjectTimeBillingDetailFragment): void {
    this.type = detail.type;
    this.title = detail.title ?? "";
    this.finalTotalNote = detail.final_total_note ?? "";
    this.selectedOiBudgetItemKeys = detail.sources
      .filter((s) => s.source_type === TimeBillingSourceTypeEnum.OiBudgetItem)
      .map((s) => {
        if (s.outgoing_invoice_id && s.budget_item_id && s.budget_item_type) {
          return encodeOiBudgetItemKey(
            s.outgoing_invoice_id,
            s.budget_item_id,
            s.budget_item_type as BudgetItemTypeEnum,
          );
        }
        return s.source_id ?? "";
      })
      .filter(Boolean);
    this.selectedExpenseItemIds = detail.sources
      .filter((s) => s.source_type === TimeBillingSourceTypeEnum.ExpenseItem)
      .map((s) => s.source_id ?? s.expenseItem?.id ?? "")
      .filter(Boolean);
    this.components = transformComponents(detail.components, this.denomination);
    this.userRows = detail.userRows.map((r) => ({
      id: r.id,
      user_id: r.user?.id,
      time_tracking_work_type_id: r.position?.id ?? null,
      team_id: r.team?.id ?? null,
      amount: NumberModel.fromDenomValue(r.amount, this.denomination),
      invoiced_time: new TimeFieldState(r.invoiced_time),
      overserviced_time: new TimeFieldState(r.overserviced_time),
      effective_hourly_rate_invoiced: NumberModel.fromDenomValue(
        r.effective_hourly_rate_invoiced,
        this.denomination,
      ),
    }));
    this.previewUserRows = null;
    this.previewSources = null;
    this.selectedTimeTrackingItemIds = detail.distributions.map(
      ({ time_tracking_item_id }) => time_tracking_item_id,
    );
  }

  @action.bound
  setType(value: TimeBillingTypeEnum): void {
    this.type = value;
    if (value === TimeBillingTypeEnum.Auto) {
      this.fetchPreview();
      return;
    }
    this.recalculateUserRows();
  }

  @action.bound
  setFinalTotalNote(value: string): void {
    this.finalTotalNote = value;
  }

  @action.bound
  setTitle(value: string): void {
    this.title = value;
  }

  @action.bound
  async confirmSourcesModalSelection(
    sourceKeys: string[],
    modalType: BudgetItemTypeEnum,
  ): Promise<void> {
    if (modalType === BudgetItemTypeEnum.OurWorkBudgetItem) {
      this.selectedOiBudgetItemKeys = sourceKeys;
    } else {
      this.selectedExpenseItemIds = sourceKeys;
    }
    await this.fetchPreview();
  }

  @action.bound
  removeOiBudgetItem(key: string): void {
    this.selectedOiBudgetItemKeys = this.selectedOiBudgetItemKeys.filter(
      (k) => k !== key,
    );
    this.fetchPreview();
  }

  @action.bound
  removeExpenseItem(id: string): void {
    this.selectedExpenseItemIds = this.selectedExpenseItemIds.filter(
      (i) => i !== id,
    );
    this.fetchPreview();
  }

  @action.bound
  async fetchPreview(options?: FetchPreviewOptions): Promise<void> {
    if (!this.detail) return;

    const sources = this.getSourcesInput();

    const applyMode: PreviewApplyMode =
      options?.applyMode ??
      (this.type === TimeBillingTypeEnum.Auto ? "full" : "sourcesOnly");

    const input: PreviewProjectTimeBillingInput = {
      project_id: this.parentStore.projectId,
      date_from: this.detail.date_from,
      date_to: this.detail.date_to,
      type: this.type,
      sources,
      components: options?.includeComponents
        ? this.getComponentsInput()
        : undefined,
      final_total_amount: this.computedTotal.denominatedValue,
      timeTrackingItemIds:
        options?.timeTrackingItemIds ??
        this.detail.distributions.map((d) => d.time_tracking_item_id),
    };

    const [data, error] = await this.previewFetcher.fetch({ input }, null);

    if (error || !data.previewProjectTimeBilling) return;

    this.applyPreviewData(data.previewProjectTimeBilling, applyMode);
  }

  @action.bound
  private applyPreviewData(
    preview: ProjectTimeBillingComputedFragment,
    mode: PreviewApplyMode,
  ): void {
    if (mode === "full") {
      this.components = transformComponents(
        preview.components,
        this.denomination,
      );
    }

    if (mode !== "userRowsOnly") {
      this.previewSources = preview.sources;
    }

    if (mode !== "sourcesOnly") {
      this.previewUserRows = preview.userRows;
      this.userRows = mapPreviewRowsToUserRows(
        preview.userRows,
        this.denomination,
      );
    }

    this.selectedTimeTrackingItemIds = preview.distributions.map(
      ({ time_tracking_item_id }) => time_tracking_item_id,
    );
  }

  @action.bound
  toggleComponentInTotal(key: TimeBillingComponentKeyEnum): void {
    this.components = this.components.map((c) =>
      c.key === key ? { ...c, use_in_total: !c.use_in_total } : c,
    );
    if (this.type === TimeBillingTypeEnum.Manual) return;
    this.recalculateUserRows();
  }

  @action.bound
  updateComponentAmount(key: TimeBillingComponentKeyEnum, value: string): void {
    this.components = this.components.map((c) =>
      c.key === key ? { ...c, amount: c.amount.setValue(value) } : c,
    );
    if (this.type === TimeBillingTypeEnum.Manual) return;
    this.debouncedRecalculateUserRows();
  }

  private debouncedRecalculateUserRows = debounce(
    () => this.recalculateUserRows(),
    300,
  );

  private debouncedRecalculateUserRow = debounce(
    (index: number, field: string, value: number | string) => {
      this.userRows = this.userRows.map((r, i) => {
        if (i !== index) return r;

        const clientRate =
          this.userRowsForDisplay[index]?.client_rate.denominatedValue ?? 0;
        const trackedTime = this.userRowsForDisplay[index]?.tracked_time ?? 0;

        if (field === "amount") {
          const newAmount = r.amount;

          if (clientRate > 0) {
            const newInvoicedTimeSeconds = Math.round(
              (newAmount.denominatedValue / clientRate) * ONE_HOUR_IN_SECONDS,
            );
            const newInvoicedTime = Math.min(
              newInvoicedTimeSeconds,
              trackedTime,
            );
            const newOverservicedTimeSeconds = Math.max(
              0,
              trackedTime - newInvoicedTime,
            );

            return {
              ...r,
              invoiced_time: new TimeFieldState(newInvoicedTime),
              overserviced_time: new TimeFieldState(newOverservicedTimeSeconds),
              effective_hourly_rate_invoiced: NumberModel.fromDenomValue(
                newInvoicedTime > 0
                  ? newAmount.denominatedValue /
                      (newInvoicedTime / ONE_HOUR_IN_SECONDS)
                  : 0,
                this.denomination,
              ),
            };
          }
          return r;
        }

        if (field === "invoiced_time") {
          const seconds = Math.max(0, typeof value === "number" ? value : 0);
          const hours = seconds / ONE_HOUR_IN_SECONDS;
          const newAmountValue = Math.round(hours * clientRate);
          const newOverservicedTimeSeconds = Math.max(0, trackedTime - seconds);

          return {
            ...r,
            amount: NumberModel.fromDenomValue(
              newAmountValue,
              this.denomination,
            ),
            overserviced_time: new TimeFieldState(newOverservicedTimeSeconds),
            effective_hourly_rate_invoiced: NumberModel.fromDenomValue(
              seconds > 0 ? Math.round(newAmountValue / hours) : 0,
              this.denomination,
            ),
          };
        }

        if (field === "overserviced_time") {
          const seconds = Math.max(0, typeof value === "number" ? value : 0);
          const newInvoicedTimeSeconds = Math.max(0, trackedTime - seconds);

          return {
            ...r,
            invoiced_time: new TimeFieldState(newInvoicedTimeSeconds),
          };
        }

        return r;
      });
    },
    300,
  );

  @action.bound
  recalculateUserRows(): void {
    const previewRows = this.previewUserRows;
    const detailRows = this.detail?.userRows;

    if (!previewRows && !detailRows) return;
    if ((previewRows?.length ?? 0) === 0 && (detailRows?.length ?? 0) === 0)
      return;

    const sourceForTotals = previewRows ?? detailRows ?? [];
    const finalTotalAmount = this.computedTotal.denominatedValue;

    const totalSpent = sourceForTotals.reduce((sum, row) => sum + row.spent, 0);

    let distributedAmount = 0;
    const totalRows = this.userRows.length;

    this.userRows = this.userRows.map((row, index) => {
      // Find the corresponding source row for tracked_time, client_rate, and spent
      // When using preview data, match by composite key (user_id + work_type_id + team_id)
      // When using detail data, match by database ID
      let trackedTime: number;
      let clientRate: number;
      let spent: number;

      if (previewRows) {
        const previewRow = previewRows.find(
          ({ user_id, time_tracking_work_type_id, team_id }) =>
            user_id === row.user_id &&
            (time_tracking_work_type_id ?? null) ===
              row.time_tracking_work_type_id &&
            (team_id ?? null) === row.team_id,
        );
        if (!previewRow) return row;
        trackedTime = previewRow.tracked_time;
        clientRate = previewRow.client_rate;
        spent = previewRow.spent;
      } else {
        const detailRow = detailRows?.find((dr) => dr.id === row.id);
        if (!detailRow) return row;
        trackedTime = detailRow.tracked_time;
        clientRate = detailRow.client_rate;
        spent = detailRow.spent;
      }

      // For the last row, assign the remaining amount to avoid rounding errors
      let amount: number;
      if (index === totalRows - 1) {
        amount = finalTotalAmount - distributedAmount;
      } else {
        amount =
          totalSpent > 0
            ? Math.round((spent / totalSpent) * finalTotalAmount)
            : 0;
      }
      distributedAmount += amount;

      let invoicedTime = 0;
      if (clientRate > 0 && spent > 0) {
        invoicedTime = Math.min(
          Math.round((amount / clientRate) * ONE_HOUR_IN_SECONDS),
          trackedTime,
        );
      }

      const overservicedTime = Math.max(0, trackedTime - invoicedTime);

      const effectiveHourlyRateInvoiced =
        invoicedTime > 0
          ? Math.round(amount / (invoicedTime / ONE_HOUR_IN_SECONDS))
          : 0;

      return {
        ...row,
        amount: NumberModel.fromDenomValue(amount, this.denomination),
        invoiced_time: new TimeFieldState(invoicedTime),
        overserviced_time: new TimeFieldState(overservicedTime),
        effective_hourly_rate_invoiced: NumberModel.fromDenomValue(
          effectiveHourlyRateInvoiced,
          this.denomination,
        ),
      };
    });
  }

  @action.bound
  updateUserRow(index: number, field: string, value: number | string): void {
    const row = this.userRows[index];
    if (!row) return;

    if (
      this.type === TimeBillingTypeEnum.Manual &&
      (field === "amount" ||
        field === "invoiced_time" ||
        field === "overserviced_time")
    ) {
      this.userRows = this.userRows.map((r, i) => {
        if (i !== index) return r;

        if (field === "amount") {
          return { ...r, amount: r.amount.setValue(value) };
        }
        if (field === "invoiced_time") {
          const seconds = Math.max(0, typeof value === "number" ? value : 0);
          return { ...r, invoiced_time: new TimeFieldState(seconds) };
        }
        if (field === "overserviced_time") {
          const seconds = Math.max(0, typeof value === "number" ? value : 0);
          return { ...r, overserviced_time: new TimeFieldState(seconds) };
        }
        return r;
      });

      this.debouncedRecalculateUserRow(index, field, value);
      return;
    }

    this.userRows = this.userRows.map((r, i) => {
      if (i !== index) return r;

      if (field === "amount") {
        return { ...r, amount: r.amount.setValue(value) };
      }

      if (field === "invoiced_time") {
        const seconds = Math.max(0, typeof value === "number" ? value : 0);
        return { ...r, invoiced_time: new TimeFieldState(seconds) };
      }

      if (field === "overserviced_time") {
        const seconds = Math.max(0, typeof value === "number" ? value : 0);
        return { ...r, overserviced_time: new TimeFieldState(seconds) };
      }

      const numValue =
        typeof value === "string" ? parseFloat(value) || 0 : value;
      return { ...r, [field]: numValue };
    });
  }

  @action.bound
  async save(): Promise<void> {
    this.debouncedRecalculateUserRow.flush();
    if (this.saveBlockedMessage) return;

    const sources = this.getSourcesInput();

    const componentsInput = this.getComponentsInput();

    const userRowsInput = this.userRows.map((row) => ({
      user_id: row.user_id,
      time_tracking_work_type_id: row.time_tracking_work_type_id,
      team_id: row.team_id,
      amount: Math.round(row.amount.denominatedValue),
      invoiced_time: Math.round(row.invoiced_time.value),
      overserviced_time: Math.round(row.overserviced_time.value),
      effective_hourly_rate_invoiced: Math.round(
        row.effective_hourly_rate_invoiced.denominatedValue,
      ),
    }));

    const timeTrackingItemIds = this.selectedTimeTrackingItemIds.map(Number);

    if (this.isUnsaved) {
      await this.parentStore.create(this.itemId, {
        project_id: this.parentStore.projectId,
        date_from: this.detail!.date_from,
        date_to: this.detail!.date_to,
        title: this.title.trim() ?? null,
        final_total_amount: this.computedTotal.denominatedValue,
        final_total_note: this.finalTotalNote || null,
        source_ids: sources,
        components: componentsInput,
        user_rows: userRowsInput,
        timeTrackingItemIds,
      });
    } else {
      const input: UpdateProjectTimeBillingInput = {
        id: this.itemId,
        type: this.type,
        title: this.title.trim() ?? null,
        final_total_amount: this.computedTotal.denominatedValue,
        final_total_note: this.finalTotalNote || null,
        source_ids: sources,
        components: componentsInput,
        user_rows: userRowsInput,
        timeTrackingItemIds,
      };

      await this.parentStore.update(input);
    }
  }

  private getSourcesInput(): TimeBillingSourceInput[] {
    const sources: TimeBillingSourceInput[] = [];

    for (const key of this.selectedOiBudgetItemKeys) {
      const { outgoing_invoice_id, budget_item_id, budget_item_type } =
        decodeOiBudgetItemKey(key);

      sources.push({
        source_type: TimeBillingSourceTypeEnum.OiBudgetItem,
        outgoing_invoice_id,
        budget_item_id,
        budget_item_type,
        include: true,
      });
    }

    for (const id of this.selectedExpenseItemIds) {
      sources.push({
        source_type: TimeBillingSourceTypeEnum.ExpenseItem,
        source_id: id,
        budget_item_type: this.getExpenseBudgetItemType(id),
        include: true,
      });
    }

    return sources;
  }

  private getComponentsInput(): TimeBillingComponentInput[] {
    return this.components.map((component) => ({
      key: component.key,
      amount: component.amount.denominatedValue,
      use_in_total: component.use_in_total,
    }));
  }

  private getExpenseBudgetItemType(
    sourceId: string,
  ): Maybe<BudgetItemTypeEnum> {
    const sources = this.previewSources ?? this.detail?.sources ?? [];

    const matchingSource = sources.find(
      (source) =>
        source.source_type === TimeBillingSourceTypeEnum.ExpenseItem &&
        (source.source_id === sourceId || source.expenseItem?.id === sourceId),
    );

    if (!matchingSource) return undefined;

    return matchingSource.budget_item_type;
  }

  @action.bound
  cancel(): void {
    if (this.isUnsaved) {
      this.parentStore.removeUnsavedItem(this.itemId);
    } else if (this.detail) {
      this.syncWithDetail(this.detail);
    }
    this.parentStore.cancelEditing();
  }

  @action.bound
  startEditing(): void {
    this.parentStore.startEditing(this.itemId);
  }

  @action.bound
  openDistributionsModal(): void {
    const detail = this.detail;
    if (!detail) return;

    this.parentStore.distributionsModalStore.disclosureState.onOpen({
      allowSelect: this.isEditing,
      dateFrom: detail.date_from,
      dateTo: detail.date_to,
      initialSelectedTotalAmount: this.userRowsTotalSpent.denominatedValue,
      initialSelectedTimeTrackingItemIds: this.selectedTimeTrackingItemIds,
      timeBillingPlanId: this.itemId,
      onConfirm: async (timeTrackingItemIds: string[]) => {
        await this.fetchPreview({
          includeComponents: true,
          timeTrackingItemIds,
          applyMode: "userRowsOnly",
        });
      },
    });
  }
}
